import {Injectable} from '@angular/core';
import {SimpleWord} from "../../models/simple-word.model";
import {Observable, throwError} from "rxjs";
import {ApiService} from "../api/api.service";
import {catchError, map} from "rxjs/operators";
import {SASEUNG3_WORDS_API_URL} from "../../defines/protocol";

export type HanjaWordInfo = {
  hanja: string,
  word_total_count: number,
  words: Array<SimpleWord>,
}


/**
 * 한자 단어 정보 조회 서비스
 *
 * saseung3 경량 API (v1/saseung3/words/)를 사용하여
 * 한자에 대한 단어 목록을 조회합니다.
 * 획수/부수 정보가 필요 없는 단어 조회에 최적화되어 있습니다.
 */
@Injectable({
  providedIn: 'root'
})
export class HanjaService {

  constructor(
    private api: ApiService,
  ) {
  }

  public GetHanjaWordInfo(hanja: string, hangul?: string): Observable<HanjaWordInfo> {
    let url = `${SASEUNG3_WORDS_API_URL}${hanja}/`;
    // 음(hangul)은 한자(hanja)가 있을 때만 추가할 수 있는 선택 옵션이다.
    // 음이 있으면 관련 단어를 음 기준으로 필터링하도록 쿼리로 전달하고,
    // 음이 없으면 기존과 동일하게 파라미터 없이 호출한다. (api#260의 동작과 정합)
    if (hanja && hangul) {
      const queryParams = new URLSearchParams();
      queryParams.set('hangul', hangul);
      url += `?${queryParams.toString()}`;
    }
    const startTime = Date.now();

    console.log('HanjaService::GetHanjaWordInfo() START', {
      url,
      hanja,
      hangul,
      apiVersion: 'saseung3'
    });

    return this.api.Get(url).pipe(
      map(data => {
        const duration = Date.now() - startTime;

        // API 응답 구조 검증
        if (!data || typeof data !== 'object') {
          console.error('HanjaService::GetHanjaWordInfo: Invalid response structure', {
            url, hanja, data
          });
          throw new Error('Invalid API response structure');
        }

        if (!data.hanja || !Array.isArray(data.words)) {
          console.error('HanjaService::GetHanjaWordInfo: Missing required fields', {
            url,
            hanja,
            hasHanja: !!data.hanja,
            hasWords: !!data.words,
            wordsIsArray: Array.isArray(data.words),
            data
          });
          throw new Error('API response missing required fields');
        }

        console.log('HanjaService::GetHanjaWordInfo SUCCESS', {
          hanja,
          wordCount: data.words.length,
          totalCount: data.word_total_count ?? 0,
          duration: `${duration}ms`
        });

        try {
          return {
            hanja: data.hanja,
            word_total_count: data.word_total_count ?? 0,
            words: data.words.map((word: any, index: number) => {
              try {
                return new SimpleWord(word);
              } catch (wordError) {
                console.error('HanjaService::GetHanjaWordInfo: Error creating SimpleWord', {
                  url, hanja, wordIndex: index, word, error: wordError
                });
                throw wordError;
              }
            })
          };
        } catch (mappingError) {
          console.error('HanjaService::GetHanjaWordInfo: Data mapping failed', {
            url, hanja, data, error: mappingError
          });
          throw mappingError;
        }
      }),
      catchError(error => {
        const duration = Date.now() - startTime;
        console.error('HanjaService::GetHanjaWordInfo FAILED', {
          hanja,
          url,
          duration: `${duration}ms`,
          status: error?.status,
          statusText: error?.statusText,
          message: error?.message,
          detail: error?.error?.detail,
          error
        });
        return throwError(() => error);
      })
    );
  }
}
